import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscription } from '../hooks/subscription/use-subscription';

function getUsageColor(remainingPercent: number): 'success' | 'warning' | 'error' {
  if (remainingPercent > 50) {
    return 'success';
  }
  if (remainingPercent > 20) {
    return 'warning';
  }
  return 'error';
}

function getColorHex(remainingPercent: number): string {
  if (remainingPercent > 50) {
    return '#22c55e';
  }
  if (remainingPercent > 20) {
    return '#eab308';
  }
  return '#ef4444';
}

function formatTimeUntilRenewal(weekPeriodEnd: string | null, t: TFunction): string {
  if (!weekPeriodEnd) {
    return '';
  }

  const now = new Date();
  const end = new Date(weekPeriodEnd);
  const diffMs = end.getTime() - now.getTime();

  if (diffMs <= 0) {
    return '';
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  const daysKey =
    days === 1
      ? 'subscription.extractionCounter.dayOne'
      : 'subscription.extractionCounter.dayOther';
  const hoursKey =
    hours === 1
      ? 'subscription.extractionCounter.hourOne'
      : 'subscription.extractionCounter.hourOther';

  const daysLabel = t(daysKey, { count: days });
  const hoursLabel = t(hoursKey, { count: hours });

  if (days > 0) {
    return t('subscription.extractionCounter.daysAndHours', { days: daysLabel, hours: hoursLabel });
  }

  return hoursLabel;
}

export function ExtractionUsageCounter() {
  const { t } = useTranslation();
  const { aiExtractionLimit, currentWeekUsage, weekPeriodEnd } = useSubscription();

  const remaining = Math.max(0, aiExtractionLimit - currentWeekUsage);
  const remainingPercent = aiExtractionLimit > 0 ? (remaining / aiExtractionLimit) * 100 : 0;
  const color = getUsageColor(remainingPercent);
  const colorHex = getColorHex(remainingPercent);

  const timeUntilRenewal = formatTimeUntilRenewal(weekPeriodEnd, t);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        borderRadius: 2,
        bgcolor: 'rgba(0,0,0,0.03)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: '1rem', color: colorHex }} />
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
        <Trans
          i18nKey="subscription.extractionCounter.remaining"
          values={{ remaining, total: aiExtractionLimit }}
          components={{
            strong: (
              <Typography component="span" sx={{ fontWeight: 700, color: `${color}.main` }} />
            ),
          }}
        />
        {timeUntilRenewal && (
          <>
            {' — '}
            <Typography component="span" sx={{ fontSize: '0.75rem' }}>
              {t('subscription.extractionCounter.renewalIn', { time: timeUntilRenewal })}
            </Typography>
          </>
        )}
      </Typography>
    </Box>
  );
}
